import { Box, BoxProps, Flex, FlexProps, HStack } from '@chakra-ui/react'
import * as R from 'ramda'
import React, { useCallback, useState } from 'react'

import {
  AnimatedList,
  Pill,
  RouterButton,
  RouterLinkProps,
} from '~shared-components/components'
import { IconMenu } from '~shared-components/icons/brand/IconMenu'
import { IconTrack } from '~shared-components/icons/brand/IconTrack'
import { IconUser } from '~shared-components/icons/brand/IconUser'
import { IconUserCheck } from '~shared-components/icons/brand/IconUserCheck'

import { useTranslations } from '../../hooks/useTranslations'
import LogoIcon from '../../theme/svgs/logo-icon.svg'
import Cart from './Cart'
import { ClickOutsideContainer } from './ClickOutsideContainer'
import { HeaderProps } from './Header'

const HeaderContainer: React.FC<BoxProps> = ({ children }) => (
  <Box
    backgroundColor="whiteAlpha.400"
    boxShadow="box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1)"
    color="neutral.90"
    display={{ base: 'block', md: 'none' }}
    transition="top 0.2s"
    width="100%"
    zIndex="100"
  >
    {children}
  </Box>
)

const sxHeaderTop = {
  '> *:not(:first-child)': {
    marginTop: '30px',
  },
}

const HeaderTop: React.FC<BoxProps> = ({ children }) => (
  <Box css={sxHeaderTop} px={0} py={2}>
    {children}
  </Box>
)

const MenuButtonContainer: React.FC<FlexProps> = ({ children }) => (
  <HStack alignItems="center" spacing={[2, 3, 5]}>
    {children}
  </HStack>
)

const MenuButtonAndControls: React.FC<FlexProps> = ({ children }) => (
  <Flex justifyContent="space-between">{children}</Flex>
)

const sxLogoContainer = {
  '> svg': {
    marginLeft: '20px',
  },
  div: {
    fontSize: '11px',
  },
}

const LogoContainer: React.FC<FlexProps> = ({ children }) => (
  <Flex alignItems="center" sx={sxLogoContainer}>
    {children}
  </Flex>
)

const sxControlContainer = {
  '> *:not(:first-child)': {
    marginLeft: '32px',
    marginTop: '2px',
  },

  button: {
    cursor: 'pointer',
  },
  svg: {
    color: 'neutral.70',
    display: 'flex',
    fill: 'neutral.70',
  },
}

const HamburgerMenuButton: React.FC<FlexProps> = ({ children, ...props }) => (
  <Flex
    cursor="pointer"
    flexDirection="column"
    height="14px"
    justifyContent="space-between"
    transition="0.2s"
    width="16px"
    {...props}
  >
    {children}
  </Flex>
)

const ControlContainer: React.FC<FlexProps> = ({ children }) => (
  <Flex
    alignItems="center"
    className="header-right"
    flexDirection="row"
    pr={{ base: '10px' }}
    sx={sxControlContainer}
  >
    {children}
  </Flex>
)

const sxExpandedMenu = {
  '> *:first-child': {
    borderBottom: '1px solid',
    borderBottomColor: 'neutral.20',
    borderTop: '1px solid',
    borderTopColor: 'neutral.20',
  },
}

const sxMenuItem = {
  'a > button': { paddingLeft: 0 },
  'a,button': {
    alignItems: 'baseline',
    color: 'neutral.90',
    cursor: 'pointer',
    display: 'flex',
    fontSize: 'md',
    justifyContent: 'left',
    lineHeight: '40px',
    px: 9,
    textAlign: 'left',
    transition: '0.2s',
    w: '100%',
  },
  'a.active, a:hover': {
    background: 'neutral.10',
  },
  'button > svg': { display: 'inline', marginRight: 3 },
}

interface MenuItemProps {
  onClick: VoidFunction
}
const MenuItem: React.FC<MenuItemProps> = ({ children, onClick }) => (
  <Flex onClick={onClick} sx={sxMenuItem}>
    {children}
  </Flex>
)

type MainMenuProps = { onClickMenuItem: () => void } & Pick<
  HeaderProps,
  'topMenuLinks'
>

const MainMenu = ({ onClickMenuItem, topMenuLinks }: MainMenuProps) => (
  <Box py="10px">
    <AnimatedList>
      {R.flatten(
        topMenuLinks.map((link, index) => [
          <MenuItem key={index} onClick={onClickMenuItem}>
            {link}
          </MenuItem>,
        ]),
      )}
    </AnimatedList>
  </Box>
)

type ProfileMenuProps = {
  onClickMenuItem: () => void
  onLogout?: () => void
  profileLink?: RouterLinkProps['to']
}

const ProfileMenu = ({
  onClickMenuItem,
  onLogout,
  profileLink,
}: ProfileMenuProps) => {
  const t = useTranslations()
  return (
    <Box py="10px">
      <AnimatedList>
        {[
          profileLink && (
            <MenuItem key="profile" onClick={onClickMenuItem}>
              <RouterButton
                leftIcon={<IconUser boxSize="20px" color="neutral.30" />}
                to={profileLink}
              >
                {t('label.profile')}
              </RouterButton>
            </MenuItem>
          ),
          <MenuItem key="logout" onClick={onClickMenuItem}>
            {onLogout ? (
              <button onClick={onLogout}>
                <IconTrack boxSize="16px" color="neutral.70" mt="4px" />
                <span>{t('label.logout')}</span>
              </button>
            ) : (
              <a href="/logout">
                <IconTrack boxSize="16px" color="neutral.70" mt="4px" />
                <span>{t('label.logout')}</span>
              </a>
            )}
          </MenuItem>,
        ]}
      </AnimatedList>
    </Box>
  )
}

const MobileHeader = ({
  cartLink,
  environmentTag,
  isCsrUi,
  isLoggedIn,
  onLogout,
  profileLink,
  search,
  topMenuLinks,
}: HeaderProps) => {
  const [isMainMenuExpanded, setIsMainMenuExpanded] = useState(false)
  const [isProfileMenuExpanded, setIsProfileMenuExpanded] = useState(false)

  const collapseMainMenu = useCallback(() => setIsMainMenuExpanded(false), [])
  const collapseProfileMenu = useCallback(
    () => setIsProfileMenuExpanded(false),
    [],
  )

  return (
    <HeaderContainer>
      <Box px={[5, 5, 20]}>
        <HeaderTop>
          <MenuButtonAndControls>
            <MenuButtonContainer>
              <HamburgerMenuButton
                justifyContent="center"
                onClick={() => {
                  setIsMainMenuExpanded(!isMainMenuExpanded)
                  collapseProfileMenu()
                }}
                transform={
                  isMainMenuExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
                }
              >
                <IconMenu boxSize="20px" color="neutral.70" />
              </HamburgerMenuButton>
              <LogoContainer py={2}>
                <LogoIcon height="40px" width="40px" />
              </LogoContainer>
              {isCsrUi && (
                <Pill bgColor="deepPurple" variant="chip-solid">
                  Admin
                </Pill>
              )}
              {environmentTag}
            </MenuButtonContainer>
            {search}
            <ControlContainer>
              {isLoggedIn ? (
                <button
                  onClick={() => {
                    setIsProfileMenuExpanded(!isProfileMenuExpanded)
                    collapseMainMenu()
                  }}
                >
                  <IconUserCheck boxSize="20px" color="neutral.70" />
                </button>
              ) : (
                <IconUser boxSize="20px" color="neutral.70" />
              )}
              {cartLink && <Cart cartLink={cartLink} />}
            </ControlContainer>
          </MenuButtonAndControls>
        </HeaderTop>
      </Box>
      <Box maxHeight="80vh" sx={sxExpandedMenu}>
        {isMainMenuExpanded && (
          <ClickOutsideContainer onClick={collapseMainMenu}>
            <MainMenu
              onClickMenuItem={collapseMainMenu}
              topMenuLinks={topMenuLinks}
            />
          </ClickOutsideContainer>
        )}
        {isProfileMenuExpanded && (
          <ClickOutsideContainer onClick={collapseProfileMenu}>
            <ProfileMenu
              onClickMenuItem={collapseProfileMenu}
              onLogout={onLogout}
              profileLink={profileLink}
            />
          </ClickOutsideContainer>
        )}
      </Box>
    </HeaderContainer>
  )
}

export default MobileHeader
